import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import Loader from './components/Loader';
import { ModalProvider, useModal } from './Context/ModalContext';
import StartupCard from './components/StartupCard';
import { AuthProvider, useAuth } from './Context/AuthContext';
import { ChatProvider } from './Context/ChatContext';
import FloatingChatWindow from './components/Chat/FloatingChatWindow';
import { updateUserPresence } from './services/userService';
import { ThemeProvider } from './Context/ThemeContext';
import ProtectedRoute from './services/ProtectedRoute';
import './styles/policy-content.css';
// Lazy load components
const LandingPage = lazy(() => import('./pages/LandingPage/LandingPage'));
const LoginPage = lazy(() => import('./pages/LoginPage/LoginPage'));
const SignupPage = lazy(() => import('./pages/SignupPage/SignupPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage/ProfilePage'));
const HomePage = lazy(() => import('./pages/HomePage/HomePage'));
const StartupPage = lazy(() => import('./pages/StartUpPage/StartUpPage'));
const StartupFeedPage = lazy(() => import('./pages/StartupFeedPage/StartupFeedPage'));
const ForgetPasswordPage = lazy(() => import('./pages/ForgetPasswordPage/ForgetPasswordPage'));
const NotificationPage = lazy(() => import('./pages/NotificationPage/NotificationPage'));
const Profilesetup = lazy(() => import('./pages/Profilesetup/Profilesetup'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage/NotFoundPage'));
const SubscriptionPlansPage = lazy(() => import('./pages/SubscriptionPage/SubscriptionPlans')); // Renamed for clarity
const PaymentPage = lazy(() => import('./pages/SubscriptionPage/PaymentPage'));
const ServicesPage = lazy(() => import('./pages/Services/Services'));
const ServiceDetailPage = lazy(() => import('./pages/Services/ServiceDetail'));
const ServiceRequestPage = lazy(() => import('./pages/Services/ServiceRequest'));
const OnePagerPage = lazy(() => import('./pages/StartUpPage/OnePagerPage'));
const NotificationsPage = lazy(() => import('./components/NotificationsPage'));

// Global StartupCard Wrapper
const StartupCardWrapper = () => {
  const { isStartupCardOpen, closeStartupCard } = useModal();

  return (
    isStartupCardOpen && (
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
        <StartupCard setShowCard={closeStartupCard} showCard={isStartupCardOpen} />
      </div>
    )
  );
};

const AppContent = () => {
  const location = useLocation();
  const { currentUser } = useAuth();

  // List of routes where chat should NOT appear
  const noChatRoutes = [
    '/',
    '/LoginPage',
    '/SignUpPage',
    '/Profilesetup',
    '/forgotpassword',
    '/NotFoundPage',
    '/subscription',
  ];

  // Check if current route should show chat
  const shouldShowChat = currentUser && !noChatRoutes.includes(location.pathname);

  return (
    <>
      <ThemeProvider>
      <StartupCardWrapper />
      {shouldShowChat && <FloatingChatWindow />}
      <Routes>
        <Route path='/' element={<LandingPage />} />
        <Route path='/LoginPage' element={<LoginPage />} />
        <Route path='/SignUpPage' element={<SignupPage />} />
        <Route path='/profile/:id' element={<ProfilePage />} />
        <Route path='/home' element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
        <Route path="/startup/:startupId" element={<ProtectedRoute><StartupPage /></ProtectedRoute>} />
        <Route path="/startupfeed" element={<ProtectedRoute><StartupFeedPage /></ProtectedRoute>} />
        <Route path="/forgotpassword" element={<ForgetPasswordPage />} />
        <Route path="/notification" element={<ProtectedRoute><NotificationPage /></ProtectedRoute>} />
        <Route path="/Profilesetup" element={<ProtectedRoute><Profilesetup /></ProtectedRoute>} />
        <Route path='/subscription' element={<ProtectedRoute><SubscriptionPlansPage /></ProtectedRoute>} />
        <Route path='/subscription/payment' element={<ProtectedRoute><PaymentPage /></ProtectedRoute>} />
        <Route path="/services" element={<ProtectedRoute><ServicesPage /></ProtectedRoute>} />
        <Route path="/services/request" element={<ProtectedRoute><ServiceRequestPage /></ProtectedRoute>} />
        <Route path="/services/:id" element={<ProtectedRoute><ServiceDetailPage /></ProtectedRoute>} />
        <Route path="/services/:category" element={<ProtectedRoute><ServicesPage /></ProtectedRoute>} />
        <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
        <Route 
            path="/view/startup/:startupId" 
            element={
              <Suspense fallback={<Loader />}>
                <OnePagerPage />
              </Suspense>
            } 
          />
          
        {/* We'll add the /subscription/manage route later */}
        <Route path='/*' element={<NotFoundPage />} />
      </Routes>
      </ThemeProvider>
    </>
  );
};

function App() {
  const { currentUser } = useAuth();

  // Effect to handle presence updates based on auth state
  useEffect(() => {
    if (currentUser?.uid) {
      // User is logged in - mark as online
      updateUserPresence(currentUser.uid, true);

      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          updateUserPresence(currentUser.uid, true);
        } else {
          updateUserPresence(currentUser.uid, false);
        }
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);

      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        if (currentUser?.uid) {
          updateUserPresence(currentUser.uid, false);
        }
      };
    }
  }, [currentUser?.uid]);

  return (
    <AuthProvider>
      <ModalProvider>
        <ChatProvider>
          <BrowserRouter>
            <Suspense fallback={<Loader />}>
              <AppContent />
            </Suspense>
          </BrowserRouter>
        </ChatProvider>
      </ModalProvider>
    </AuthProvider>
  );
}

export default App;